import { Box, Text } from '@opendoor/bricks-next';
import { EntryComponent } from 'cms/entries/entries';

import {
  ILpComponentOptionsComparison,
  ILpComponentOptionsComparisonCard,
} from '../../declarations/contentful';
import Container from './shared/Container';
import { BNColorThemes, getBNComponentThemeColors } from './shared/styles/ComponentTheme';

// Check icon SVG - using green from reference design
const CheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#00A86B"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <path d="M5 13l4 4L19 7" />
  </svg>
);

// Badge positioned absolutely at top of card, overlapping the border
const Badge = ({
  id,
  text,
  isHighlighted,
}: {
  id?: string;
  text: string;
  isHighlighted: boolean;
}) => (
  <div
    id={id}
    style={{
      position: 'absolute',
      top: -14,
      left: 24,
      backgroundColor: isHighlighted ? '#0066FF' : '#4A4A4A',
      borderRadius: 100,
      paddingLeft: 14,
      paddingRight: 14,
      paddingTop: 6,
      paddingBottom: 6,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: '#FFFFFF',
        lineHeight: 1,
      }}
    >
      {text}
    </span>
  </div>
);

// Bullet point with green checkmark
const BulletPoint = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      marginBottom: 10,
    }}
  >
    <div style={{ flexShrink: 0, marginTop: 2 }}>
      <CheckIcon />
    </div>
    <Text tag="span" typography="$bodySmall" color="$contentSecondary">
      {children}
    </Text>
  </div>
);

// "How it works" section at bottom of card
const HowItWorksSection = ({
  id,
  label,
  text,
  isHighlighted,
}: {
  id?: string;
  label: string;
  text: string;
  isHighlighted: boolean;
}) => (
  <div
    id={id}
    style={{
      backgroundColor: isHighlighted ? 'rgba(0, 102, 255, 0.06)' : '#F7F7F7',
      borderRadius: 8,
      padding: 12,
      marginTop: 16,
    }}
  >
    <p
      style={{
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: '#717171',
        marginTop: 0,
        marginBottom: 6,
      }}
    >
      {label}
    </p>
    <p
      style={{
        fontSize: 12,
        color: '#222222',
        lineHeight: 1.5,
        margin: 0,
      }}
    >
      {text}
    </p>
  </div>
);

// Sanitize a string to be a valid HTML ID
const sanitizeHtmlId = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD') // Normalize Unicode characters
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9-]/g, '-') // Replace non-alphanumeric with dashes
    .replace(/-+/g, '-') // Collapse consecutive dashes
    .replace(/^-|-$/g, ''); // Trim leading/trailing dashes
};

const OptionCard = ({ card }: { card: ILpComponentOptionsComparisonCard }) => {
  const { fields, sys } = card;
  const isHighlighted = fields.isHighlighted || false;

  // Generate stable ID for GTM tracking - prefer sys.id, fallback to sanitized title
  const cardId = sys?.id || (fields.title ? sanitizeHtmlId(fields.title) : undefined);
  const badgeId = cardId ? `${cardId}-badge` : undefined;
  const howItWorksId = cardId ? `${cardId}-how-it-works` : undefined;

  const bulletPoints = [fields.bulletPoint1, fields.bulletPoint2, fields.bulletPoint3].filter(
    Boolean,
  ) as string[];

  return (
    <div
      id={cardId}
      style={{
        position: 'relative',
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        border: `2px solid ${isHighlighted ? '#0066FF' : '#E8E8E8'}`,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Badge overlapping the top border */}
      <Badge id={badgeId} text={fields.badge} isHighlighted={isHighlighted} />

      {/* Title */}
      <Text tag="h3" typography="$subheaderXsmall" color="$contentPrimary" mb="$8x" mt="$4x">
        {fields.title}
      </Text>

      {/* Description/tagline with bottom border */}
      {fields.description && (
        <div
          style={{
            paddingBottom: 14,
            marginBottom: 16,
            borderBottom: '1px solid #E8E8E8',
          }}
        >
          <Text tag="p" typography="$bodySmall" color="$contentSecondary">
            {fields.description}
          </Text>
        </div>
      )}

      {/* Bullet points - flex grow to push "how it works" to bottom */}
      {bulletPoints.length > 0 && (
        <div style={{ flex: 1, paddingTop: 8 }}>
          {bulletPoints.map((bullet, idx) => (
            <BulletPoint key={idx}>{bullet}</BulletPoint>
          ))}
        </div>
      )}

      {/* How it works section */}
      {fields.howItWorksText && (
        <HowItWorksSection
          id={howItWorksId}
          label={fields.howItWorksLabel || 'How it works'}
          text={fields.howItWorksText}
          isHighlighted={isHighlighted}
        />
      )}
    </div>
  );
};

const RenderOptionsComparison = (entry: ILpComponentOptionsComparison) => {
  const { fields, sys } = entry;

  const bgColor = fields.bgColor || 'Grey';
  const theme = getBNComponentThemeColors(
    bgColor && bgColor in BNColorThemes ? (bgColor as BNColorThemes) : BNColorThemes.Light,
  );

  // Filter out null entries (can occur when referenced cards are unpublished/deleted)
  const options = (fields.options || []).filter(Boolean);

  // Generate stable ID for GTM tracking
  const componentId = sys?.id || 'options-comparison';

  return (
    <Container
      id={componentId}
      backgroundColor={theme.bgPrimary}
      py="$40x"
      $smallerThanMD={{ py: '$24x' }}
    >
      {/* Header Section */}
      <Box mb="$40x">
        {fields.eyebrow && (
          <Text
            tag="p"
            typography="$bodySmall"
            fontWeight="$medium"
            color="$contentTertiary"
            mb="$8x"
          >
            {fields.eyebrow}
          </Text>
        )}
        {fields.title && (
          <Text
            tag={fields.titleTag || 'h2'}
            typography="$subheaderMedium"
            color={theme.contentPrimary}
            mb="$4x"
          >
            {fields.title}
            {fields.titleAccent && (
              <Text tag="span" typography="$subheaderMedium" color="$contentTertiary">
                {` ${fields.titleAccent}`}
              </Text>
            )}
          </Text>
        )}
        {fields.subtitle && (
          <Text tag="p" typography="$bodyMedium" color="$contentSecondary" mt={14}>
            {fields.subtitle}
          </Text>
        )}
      </Box>

      {/* Cards Grid */}
      {options.length > 0 && (
        <Box
          display="flex"
          flexDirection="row"
          gap="$24x"
          alignItems="stretch"
          $smallerThanMD={{
            flexDirection: 'column',
          }}
        >
          {options.map((card, idx) => (
            <Box key={card.sys?.id || idx} flex={1}>
              <OptionCard card={card} />
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
};

const OptionsComparison: EntryComponent<ILpComponentOptionsComparison> = {
  render: RenderOptionsComparison,
};

export { RenderOptionsComparison, sanitizeHtmlId };
export default OptionsComparison;
